import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { AppMetaData, AppMetaDataTab, COLLECTION_ITEM_TYPE_SCREEN, COLLECTION_ITEM_TYPE_COLLECTION } from '../api/types'
import { Language } from '../settings/types'

export interface ScreenNavigationParams {
	itemId: number
	itemVersion?: number
	title?: string // optional as only used by native platform currently
	accountId?: number
}

export interface CollectionListNavigationParams {
	collectionId: number
	collectionVersion?: number
	collectionTitle?: string // optional as only used by native platform currently - displayed in header
	isTopCollection?: boolean // optional as only used by native platform currently - needed a way to only show back button in header on sub collections
}

export interface TabConfig {
	accountId?: number
	tabId?: number
	tabBarTitle: string
	tabBarIcon: string
	isCustomIcon?: boolean
	key?: string // used to set unique key on reused routes in a stack (eg CollectionList) and also use to create unique route name for top level routes/tabs (might have multiple Collection or Screen tabs)
	params: CollectionListNavigationParams | ScreenNavigationParams
	type: GetCollectionItemsIndexResponseData.ItemTypeEnum
	subtype: GetCollectionItemsIndexResponseData.ItemSubtypeEnum
}

export function convertMetaDataToTabConfig(metaData: AppMetaData, selectedLanguage?: Language): TabConfig[] {
	const currentLanguage: string | undefined = selectedLanguage ? selectedLanguage.code : undefined
	const primaryLanguage: string = metaData.primary_language!.toString()

	const result: TabConfig[] = []
	if (metaData.tabs) {
		for (const tab of metaData.tabs as AppMetaDataTab[]) {
			let name = ''
			if (tab.short_title) {
				if (currentLanguage && tab.short_title[currentLanguage]) {
					name = tab.short_title[currentLanguage]
				} else if (tab.short_title[primaryLanguage]) {
					name = tab.short_title[primaryLanguage]
				}
			}
			if (tab.tab_type === COLLECTION_ITEM_TYPE_COLLECTION) {
				const collectionId = tab.collection_id
				if (collectionId === undefined) {
					throw Error('Must supply collectionId in meta data')
				}
				if (
					[
						GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Story,
						GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web,
						GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama,
					].some(item => item === tab.subtype)
				) {
					throw Error('Invalid subtype for collection: ' + tab.subtype)
				}
				// if tab has no icon, use default grid icon
				const icon: string = tab.icon || 'th-large'
				const config: TabConfig = {
					accountId: tab.account_id,
					tabId: tab.tab_id,
					tabBarTitle: name,
					tabBarIcon: icon,
					isCustomIcon: tab.is_custom_icon,
					key: collectionId.toString(),
					params: {
						collectionId,
						collectionVersion: tab.version || Number(),
						collectionTitle: name,
						isTopCollection: true,
					},
					type: GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection,
					subtype: tab.subtype,
				}
				result.push(config)
			} else if (tab.tab_type === COLLECTION_ITEM_TYPE_SCREEN) {
				const screenId = tab.screen_id
				if (screenId === undefined) {
					throw Error('Must supply screenId in meta data')
				}
				if (tab.subtype === 'list' || tab.subtype === 'tour' || tab.subtype === 'menu') {
					throw Error('Invalid subtype for screen: ' + tab.subtype)
				}
				// if tab has no icon, use default information icon
				const icon: string = tab.icon || 'info-square'
				const config: TabConfig = {
					accountId: tab.account_id,
					tabId: tab.tab_id,
					tabBarTitle: name,
					tabBarIcon: icon,
					isCustomIcon: tab.is_custom_icon,
					key: screenId.toString(),
					params: { itemId: screenId, itemVersion: tab.version || Number(), title: name },
					type: GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen,
					subtype: tab.subtype,
				}
				result.push(config)
			}
		}
	}
	return result
}
